import type { Department, LeaveType, Position, StaffProfile } from "../../services/staffService";
import { byNumber, byText, describeUsage, reversed, type RegisterConfig } from "../register/registerLogic";

export { describeUsage };

/**
 * The three HR setup registers -- departments, positions, leave policies --
 * and the counts that make them readable: who is in post where, and what
 * would stop a record being deleted.
 */

/** Still employed: counts towards a department or a post. Resigned, retired
 * and terminated staff keep their profile (and so block a delete) but are not
 * in post. */
export const IN_POST_STATUSES = ["active", "probation", "suspended"];
/** Who can be made a department head. */
export const HEAD_ELIGIBLE_STATUSES = ["active", "probation"];

export const inPost = (profile: Pick<StaffProfile, "employment_status">) =>
  IN_POST_STATUSES.includes(profile.employment_status);

export const DEPARTMENT_TYPES: [Department["department_type"], string][] = [
  ["academic", "Academic"],
  ["administrative", "Administrative"],
  ["finance", "Finance"],
  ["student_services", "Student services"],
  ["operations", "Operations"],
  ["other", "Other"],
];
export const departmentTypeLabel = (type: string) =>
  DEPARTMENT_TYPES.find(([value]) => value === type)?.[1] ?? "Other";

export const POSITION_LEVELS: [string, string][] = [
  ["entry", "Entry"],
  ["junior", "Junior"],
  ["senior", "Senior"],
  ["manager", "Manager"],
  ["director", "Director"],
  ["executive", "Executive"],
];
export const levelLabel = (level: string) =>
  POSITION_LEVELS.find(([value]) => value === level)?.[1] ?? level;
const levelRank = (level: string) => POSITION_LEVELS.findIndex(([value]) => value === level);


/**
 * Which actions a setup record should offer.
 *
 * Deactivating is always available -- it is reversible and safe. Deleting is
 * offered only when the server has said it would be accepted, so the page
 * never shows a button that is going to come back refused. `can_delete`
 * absent (an older response) is treated as "not deletable", which errs
 * towards the safe action.
 */
export function recordActions(record: {
  is_active: boolean;
  can_delete?: boolean;
  in_use_by?: Record<string, number>;
}): { canDelete: boolean; toggleLabel: "Deactivate" | "Activate"; blockedBecause: string } {
  return {
    canDelete: record.can_delete === true,
    toggleLabel: record.is_active ? "Deactivate" : "Activate",
    blockedBecause: describeUsage(record.in_use_by),
  };
}

export const activeStatus = (record: { is_active: boolean }) =>
  record.is_active ? { label: "Active", dot: "bg-emerald-500" } : { label: "Inactive", dot: "bg-gray-400" };

// --- Departments -----------------------------------------------------------

export type DepartmentRow = Department & { staffCount: number; positionCount: number };

export function withDepartmentCounts(
  departments: Department[] | undefined,
  profiles: StaffProfile[] | undefined,
  positions: Position[] | undefined,
): DepartmentRow[] | undefined {
  if (!departments) return undefined;
  return departments.map((department) => ({
    ...department,
    staffCount: (profiles ?? []).filter((profile) => profile.department === department.id && inPost(profile)).length,
    positionCount: (positions ?? []).filter((position) => position.department === department.id).length,
  }));
}

export const departmentConfig: RegisterConfig<DepartmentRow> = {
  search: (row) => [row.name, row.code, departmentTypeLabel(row.department_type), row.head_name, row.description].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "active", label: "Active", match: (row) => row.is_active },
    { key: "no-head", label: "No head", match: (row) => row.is_active && !row.head },
    { key: "inactive", label: "Inactive", match: (row) => !row.is_active },
  ],
  category: { label: "Type", value: (row) => departmentTypeLabel(row.department_type) },
  sorts: [
    { key: "name", label: "Name (A–Z)", compare: byText<DepartmentRow>((row) => row.name) },
    { key: "-staff", label: "Most staff", compare: reversed(byNumber<DepartmentRow>((row) => row.staffCount)) },
    { key: "-positions", label: "Most positions", compare: reversed(byNumber<DepartmentRow>((row) => row.positionCount)) },
    { key: "code", label: "Code (A–Z)", compare: byText<DepartmentRow>((row) => row.code) },
  ],
};

// --- Positions ---------------------------------------------------------------

/** `holders` are the staff in post in this position now. The stored
 * `is_vacant` flag is set once at creation and never kept up to date, so
 * vacancy is worked out from the holders instead. */
export type PositionRow = Position & { holders: number };

export function withHolders(positions: Position[] | undefined, profiles: StaffProfile[] | undefined): PositionRow[] | undefined {
  if (!positions) return undefined;
  return positions.map((position) => ({
    ...position,
    holders: (profiles ?? []).filter((profile) => profile.position === position.id && inPost(profile)).length,
  }));
}

export const positionDepartment = (row: Pick<Position, "department_name">) => row.department_name || "School-wide";

/** "UGX 1,200,000 – 1,800,000", "From UGX 900,000", or "" when not set. */
export function salaryRange(row: Pick<Position, "salary_range_min" | "salary_range_max">) {
  const money = (value?: string | null) => Number(value || 0).toLocaleString();
  const min = Number(row.salary_range_min || 0);
  const max = Number(row.salary_range_max || 0);
  if (min && max) return `UGX ${money(row.salary_range_min)} – ${money(row.salary_range_max)}`;
  if (min) return `From UGX ${money(row.salary_range_min)}`;
  if (max) return `Up to UGX ${money(row.salary_range_max)}`;
  return "";
}

export const positionConfig: RegisterConfig<PositionRow> = {
  search: (row) => [row.title, positionDepartment(row), levelLabel(row.level), row.description, row.qualifications].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "active", label: "Active", match: (row) => row.is_active },
    { key: "vacant", label: "Vacant", match: (row) => row.is_active && row.holders === 0 },
    { key: "inactive", label: "Inactive", match: (row) => !row.is_active },
  ],
  category: { label: "Department", value: positionDepartment },
  sorts: [
    { key: "title", label: "Title (A–Z)", compare: byText<PositionRow>((row) => row.title) },
    { key: "department", label: "Department (A–Z)", compare: byText<PositionRow>((row) => `${positionDepartment(row)} ${row.title}`) },
    { key: "-level", label: "Most senior first", compare: reversed(byNumber<PositionRow>((row) => levelRank(row.level))) },
    { key: "-holders", label: "Most holders", compare: reversed(byNumber<PositionRow>((row) => row.holders)) },
  ],
};

// --- Leave policies ------------------------------------------------------------

export const leaveRequestsTaken = (policy: LeaveType) => policy.in_use_by?.["leave requests"] ?? 0;

export const leaveConfig: RegisterConfig<LeaveType> = {
  search: (row) => [row.name, row.code, row.description].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "active", label: "Active", match: (row) => row.is_active },
    { key: "inactive", label: "Inactive", match: (row) => !row.is_active },
  ],
  category: { label: "Pay", value: (row) => (row.is_paid ? "Paid" : "Unpaid") },
  sorts: [
    { key: "name", label: "Name (A–Z)", compare: byText<LeaveType>((row) => row.name) },
    { key: "-days", label: "Most days", compare: reversed(byNumber<LeaveType>((row) => row.max_days_per_year)) },
    { key: "days", label: "Fewest days", compare: byNumber<LeaveType>((row) => row.max_days_per_year) },
  ],
};
